import React, { ReactNode, useMemo } from 'react';
import './entity-selector.scss';
import { Button, Popover, Select } from 'antd';
import FormField from '../edit-row-modal/form-field/form-field';
import CustomIcon, { IconType } from '../custom-icon/custom-icon';
import { EntitySelectorItem } from './entity-popup/entity-selector-item';
import { MultiSelectType } from '../../../model/EditableRule';
import { Column, CrudConfig } from '../../../model/table';
import { EntityPopup } from './entity-popup/entity-popup';
import { FieldMessage, getErrorMessage } from '../../../dtos/error-message.dto';
import Arrow from '@svg/chevron-down_20.svg?react';
import InactiveAlertIcon from '@svg/inactive-alert.svg?react';

export interface ListItem {
    key: string;
    name: string;
    data: any;
}

export const DefaultLabel: React.FC<{ label: string }> = ({ label }) =>
    <span className={'entity-name'}>{label}</span>;

interface EntitySelectorProps<T> {
    entityName: { singular: string; plural: string };
    fullList: ListItem[];
    selectedItems: string[];
    type?: MultiSelectType;
    label?: string;
    error: FieldMessage;
    handleSelection: (key: string, checked: boolean) => void;
    handleBulkAllSelection?: (keys: string[], checked: boolean) => void;
    handleTypeSelection: (value: MultiSelectType) => void;
    handleRemove: (key: string) => void;
    EntityLabel: React.FC<{ data: any }>;
    EntityInfo?: React.FC<{ data: any }>;
    crudConfig?: CrudConfig<T>;
    columnsConfig?: Column<T>[];
    InfoMsg?: React.FC<{data: any }>;
    WarningMessage?: ReactNode;
    isTypeDisabled?: boolean;
    disabled?: boolean;
    disableSelectAny?: boolean;
    alertDeactivate?: string;
    isNewEntityCreatable?: boolean;
    AddButton?: ReactNode;
}

export const EntitySelector = <T,>(props: EntitySelectorProps<T>) => {
    const { entityName, fullList, selectedItems, type, label, error,
        handleSelection, handleBulkAllSelection, handleTypeSelection, handleRemove, EntityLabel, alertDeactivate,
        EntityInfo, crudConfig, columnsConfig, InfoMsg, WarningMessage,
        isTypeDisabled, disableSelectAny, disabled = false, isNewEntityCreatable = true, AddButton } = props;

    const handleSelectChange = (value: MultiSelectType) => {
        handleTypeSelection(value);
    };

    const dataTestId = useMemo(() =>
        entityName.plural.toLowerCase().replaceAll(' ', '-')
    , [entityName]);

    const items = useMemo(() => {
        return fullList.filter(item => selectedItems.includes(item.key)).map(item => {
            return (
                <div className={'entity-item-wrapper'} key={item.key}>
                    {<EntitySelectorItem item={item} fullList={fullList}
                                         crudConfig={crudConfig}
                                         columnsConfig={columnsConfig}
                                         entityName={entityName.singular}
                                         onRemove={(entity: ListItem) => handleRemove(entity.key)}
                                         EntityLabel={EntityLabel}
                                         InfoMsg={InfoMsg}
                                         EntityInfo={EntityInfo}
                                         removable={!disabled}/>}
                </div>
            );
        });
    }, [fullList, selectedItems]);

    return (
        <div className={'entity-selector'} data-testid={`${dataTestId}-entity-selector`}>
            <div className={'entity-selector-header'}>
                <FormField label={label} inlineLabel={true} errorMessage={getErrorMessage(error, `${entityName.plural} list is empty`)}>
                    <Select popupClassName={'dropdown-custom'} placeholder={`Select ${entityName.plural}`}
                        disabled={!!isTypeDisabled || disabled || disableSelectAny}
                        onChange={handleSelectChange}  suffixIcon={<Arrow />}
                        value={disableSelectAny ? MultiSelectType.SELECTED : type}>
                        <Select.Option value={MultiSelectType.ANY}>{`Any ${entityName.singular}`}</Select.Option>
                        <Select.Option value={MultiSelectType.SELECTED}>{`Selected ${entityName.plural}`}</Select.Option>
                    </Select>
                    {!!selectedItems.length && <span className={'selected-items-count'}>{selectedItems.length} selected</span>}
                </FormField>
                <div className={'add-entity'}>
                    <div className={'add-entity-wrapper'}>
                        {type === MultiSelectType.SELECTED && !disabled &&
                            <>
                                {AddButton ||
                                <Popover trigger="click" showArrow={false} placement={'bottomRight'} overlayClassName={`entity-popup ${dataTestId}-entity-popup`}
                                    content={
                                        <EntityPopup
                                            fullList={fullList}
                                            selectedItems={selectedItems}
                                            entityName={entityName}
                                            handleSelection={handleSelection}
                                            handleBulkAllSelection={handleBulkAllSelection}
                                            EntityLabel={EntityLabel}
                                            EntityInfo={EntityInfo}
                                            crudConfig={crudConfig}
                                            columnsConfig={columnsConfig}
                                            isNewEntityCreatable={isNewEntityCreatable}
                                        />}>
                                        <Button className={'button-link add-entity-button'}>
                                            <CustomIcon type={IconType.PLUS} size={10} />
                                            {`Add ${entityName.plural}`}
                                        </Button>
                                </Popover>
                                }
                            </>
                        }
                    </div>
                </div>
            </div>
            {alertDeactivate && <div className="alert-deactivate">
                <InactiveAlertIcon />
                <div className="alert-message">{alertDeactivate}</div>
            </div>}
            {WarningMessage}
            {type === MultiSelectType.SELECTED && !selectedItems.length &&
                <div className={'empty-list-message'}>
                    Click&nbsp;<span>{` Add ${entityName.plural} `}</span>&nbsp;to define {entityName.plural.toLowerCase()} behavior
                </div>
            }
            {!!selectedItems.length && <div className={'items-box'}>{items}</div>}
        </div>
    );
};

export default EntitySelector;